import * as React from "react";

import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";

type AutosizeTextareaProps = React.ComponentProps<"textarea"> & {
  /** 내용이 비어 있어도 유지할 최소 줄 수 */
  minRows?: number;
};

/**
 * 내용 길이에 맞춰 높이가 자동으로 늘어나는 textarea.
 * 내부 스크롤이 생기지 않으므로 작성 중인 글 전체가 항상 보인다.
 */
const AutosizeTextarea = React.forwardRef<HTMLTextAreaElement, AutosizeTextareaProps>(
  ({ className, minRows = 3, value, onChange, style, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      },
      [ref],
    );

    const fit = React.useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }, []);

    React.useLayoutEffect(() => {
      fit();
    }, [value, fit]);

    return (
      <Textarea
        ref={setRefs}
        rows={minRows}
        value={value}
        onChange={(e) => {
          onChange?.(e);
          fit();
        }}
        style={{ ...style, overflow: "hidden" }}
        className={cn("resize-none leading-relaxed", className)}
        {...props}
      />
    );
  },
);
AutosizeTextarea.displayName = "AutosizeTextarea";

export { AutosizeTextarea };
